// src/design-system/Field.tsx
import { Eye, EyeOff, Info } from "lucide-react";
import { type ReactNode, forwardRef, useId, useState } from "react";
import type { InputHTMLAttributes, SelectHTMLAttributes, TextareaHTMLAttributes } from "react";
import type { FieldError } from "react-hook-form";

/** تلميح كأيقونة (ⓘ) جنب اسم الخانة بدل نص ظاهر طول الوقت — hover على الكمبيوتر، ضغطة على الموبايل.
 * اختياري (hintAsIcon) عشان الافتراضي في كل التطبيق يفضل زي ما هو، ومُستخدم حالياً في الخطابات بس. */
function HintIcon({ text }: { text: string }) {
  const [open, setOpen] = useState(false);
  return (
    <span className="relative inline-flex">
      <button
        type="button" onClick={() => setOpen((o) => !o)} onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}
        aria-label="توضيح" aria-expanded={open} className="grid place-items-center text-muted-3 hover:text-ink"
      >
        <Info size={13} aria-hidden />
      </button>
      {open && (
        <span role="tooltip" className="absolute top-full start-0 z-20 mt-1.5 w-64 rounded-[8px] border p-2.5 text-[11.5px] leading-[1.7] text-muted-2"
          style={{ background: "var(--bg)", borderColor: "var(--color-line-2)" }}>
          {text}
        </span>
      )}
    </span>
  );
}

/** تسمية فوق الحقل ورسالة الخطأ تحته مباشرة (معيار الـ Design Brief). hintAsIcon يحوّل التلميح لأيقونة
 * بدل نص ثابت تحت الخانة — استخدمها للخانات الكبيرة (Textarea) اللي التلميح فيها طويل. */
export function Field({
  label, error, hint, children, htmlFor, hintAsIcon,
}: { label: string; error?: FieldError | string; hint?: string; children: ReactNode; htmlFor?: string; hintAsIcon?: boolean }) {
  const message = typeof error === "string" ? error : error?.message;
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={htmlFor} className="flex items-center gap-1.5 text-[12.5px] text-muted">
        {label}
        {hint && hintAsIcon && <HintIcon text={hint} />}
      </label>
      {children}
      {hint && !hintAsIcon && !message && <span className="text-[11.5px] text-muted-3">{hint}</span>}
      {message && <span role="alert" className="text-[12px] text-bad">{message}</span>}
    </div>
  );
}

type WithError = { invalid?: boolean };

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement> & WithError>(
  ({ invalid, className, type, ...rest }, ref) => {
    // كل حقل كلمة مرور في التطبيق بيعدي من هنا — زر العين مضاف مركزياً هنا مش في كل شاشة لوحده.
    const [show, setShow] = useState(false);
    if (type === "password") {
      return (
        <div className="relative">
          <input
            ref={ref} type={show ? "text" : "password"} aria-invalid={invalid || undefined}
            className={`field !pe-10 ${className ?? ""}`} {...rest}
          />
          <button
            type="button" tabIndex={-1} onClick={() => setShow((s) => !s)}
            aria-label={show ? "إخفاء كلمة المرور" : "إظهار كلمة المرور"}
            className="absolute inset-y-0 end-2.5 grid place-items-center text-muted-2 hover:text-ink"
          >
            {show ? <EyeOff size={16} aria-hidden /> : <Eye size={16} aria-hidden />}
          </button>
        </div>
      );
    }
    return <input ref={ref} type={type} className={`field ${className ?? ""}`} aria-invalid={invalid || undefined} {...rest} />;
  },
);
Input.displayName = "Input";

export const Select = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement> & WithError>(
  ({ invalid, className, children, ...rest }, ref) => (
    <select ref={ref} className={`field ${className ?? ""}`} aria-invalid={invalid || undefined} {...rest}>
      {children}
    </select>
  ),
);
Select.displayName = "Select";

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement> & WithError>(
  ({ invalid, className, ...rest }, ref) => (
    <textarea ref={ref} rows={3} className={`field ${className ?? ""}`} aria-invalid={invalid || undefined} {...rest} />
  ),
);
Textarea.displayName = "Textarea";

export const useFieldId = () => useId();
